import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";

import { getStorage, setStorage } from "../storage/storageHelper";
import { Server } from "@modules/api";
import { useAppSelector } from "@lib/stores";
import { useAppLiveQuery } from "@modules/local/hooks";
import { db, ReaderFlow } from "@modules/local/db";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";

export type B2BContextData = {
  isB2B: boolean | undefined;
  isB2bAllowed: boolean;
  isB2cAllowed: boolean;
  data: Server.GetMyUserBusinessGroupOutput[];
};

interface B2BContextProps {
  b2B: B2BContextData | null;
  toggleB2B: (isB2B: boolean) => void;
}

const B2BContext = createContext<B2BContextProps | undefined>(undefined);

export const useB2B = (): B2BContextProps => {
  const context = useContext(B2BContext);

  if (!context) {
    throw new Error("useB2B must be used within a B2BProvider");
  }
  return context;
};

interface B2BProviderProps {
  children: ReactNode;
}

export const B2BProvider = ({ children }: B2BProviderProps) => {
  const navigate = useAppNavigate();

  const [b2B, setB2B] = useState<B2BContextData | null>(null);

  const userId = useAppSelector((state) => state.auth.user.id ?? "");

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const toggleB2B = useCallback(
    async (isB2B: boolean) => {
      const b2B = await getStorage("B2B");

      const b2BData = {
        isB2B,
        data: b2B?.data || [],
        isB2bAllowed: !!flow?.tabs?.find((tab) => tab.isB2B),
        isB2cAllowed: !!flow?.tabs?.find((tab) => !tab.isB2B && !tab.isCarPlay),
      };

      await setStorage({
        key: "B2B",
        data: b2BData,
      });

      setB2B(b2BData);

      navigate("/");
    },
    [navigate, flow]
  );

  useEffect(() => {
    (async () => {
      const b2B = await getStorage("B2B");

      setB2B(getIsB2bData(flow, b2B?.isB2B, b2B?.data));
    })();
  }, [flow]);

  return (
    <B2BContext.Provider value={{ b2B, toggleB2B }}>
      {children}
    </B2BContext.Provider>
  );
};

export const getIsB2bData = (
  flow: ReaderFlow | undefined,
  isB2bSession: boolean | undefined,
  b2BData: B2BContextData["data"] | undefined
): B2BContextData => {
  const isB2bAllowed = !!flow?.tabs?.find((tab) => tab.isB2B);
  const isB2cAllowed = !!flow?.tabs?.find(
    (tab) => !tab.isB2B && !tab.isCarPlay
  );

  if (isB2bAllowed && isB2bSession)
    return {
      isB2B: true,
      isB2bAllowed,
      isB2cAllowed,
      data: b2BData ?? [],
    };

  if (isB2cAllowed && isB2bSession === false)
    return {
      isB2B: false,
      isB2bAllowed,
      isB2cAllowed,
      data: b2BData ?? [],
    };

  if (isB2bAllowed)
    return {
      isB2B: true,
      isB2bAllowed,
      isB2cAllowed,
      data: b2BData ?? [],
    };

  return {
    isB2B: undefined,
    isB2bAllowed,
    isB2cAllowed,
    data: b2BData ?? [],
  };
};
